import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import { Divider } from '@/components/Divider';
import { HeaderCard } from '@/components/layout/HeaderCard';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import { useGetNotificationPreferencesQuery } from './graphql/NotificationPreferences.generated';
import { NotificationPreferenceRow } from './NotificationPreferenceRow';
import { SystemNotificationsCard } from './SystemNotificationsCard';
import {
  COLUMN_WIDTHS,
  NOTIFICATION_KIND_ORDER,
  SYSTEM_NOTIFICATION_KINDS,
} from './utils';

export function NotificationPreferencesCard() {
  const canEditSystemPreferences = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_INTEGRATIONS_ADMIN_PAGE
  );
  const { createErrorFeedback } = useFeedback();
  const { data, loading } = useGetNotificationPreferencesQuery({
    onError: (error) => {
      diagnostics.error('failed to get notification preferences', error);
      createErrorFeedback(
        'Failed to fetch notification preferences. Please refresh the page and try again.'
      )();
    },
  });

  const { systemPreferences, regularPreferences } = useMemo(() => {
    if (!data?.notificationPreferences) {
      return { systemPreferences: [], regularPreferences: [] };
    }

    const allPreferences = getNodes(data.notificationPreferences);
    const system = allPreferences.filter((pref) =>
      SYSTEM_NOTIFICATION_KINDS.includes(pref.kind)
    );
    let regular = allPreferences.filter(
      (pref) => !SYSTEM_NOTIFICATION_KINDS.includes(pref.kind)
    );

    regular = regular.sort((a, b) => {
      const indexA = NOTIFICATION_KIND_ORDER.indexOf(a.kind);
      const indexB = NOTIFICATION_KIND_ORDER.indexOf(b.kind);
      return indexA - indexB;
    });

    return { systemPreferences: system, regularPreferences: regular };
  }, [data]);

  if (loading) {
    return (
      <Loader
        boxProps={{
          sx: {
            textAlign: 'center',
            my: 3,
          },
        }}
      />
    );
  }

  return (
    <HeaderCard
      heading="Notifications"
      subheading="Customize how to be notified of actionable items"
      sx={{ height: '100%' }}
    >
      {systemPreferences.length === 0 && regularPreferences.length === 0 ? (
        <Typography variant="body-s" color="text.secondary" sx={{ mt: 2 }}>
          No notification preferences available.
        </Typography>
      ) : (
        <Box mt={2} mb={2}>
          {/* Column Headers */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              px: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box sx={{ width: COLUMN_WIDTHS.IN_APP }}>
                <Typography variant="body-s">In-app</Typography>
              </Box>
              <Box sx={{ width: COLUMN_WIDTHS.EMAIL_FREQUENCY }}>
                <Typography variant="body-s">Email</Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
            </Box>
          </Box>

          <Stack spacing={2}>
            {/* Regular notification preferences */}
            {regularPreferences.length > 0 && (
              <Stack spacing={2} divider={<Divider />}>
                {regularPreferences.map((preference) => (
                  <NotificationPreferenceRow
                    key={preference.id}
                    preference={preference}
                  />
                ))}
              </Stack>
            )}

            {/* System notifications card */}
            {systemPreferences.length > 0 && canEditSystemPreferences && (
              <SystemNotificationsCard systemPreferences={systemPreferences} />
            )}
          </Stack>
        </Box>
      )}
    </HeaderCard>
  );
}
